import React, { useEffect, useMemo, useState } from "react";
import client from "../lib/api";
import { Card, Badge } from "./ui";
import { Users, DollarSign, ArrowUpDown, TrendingUp } from "lucide-react";

const TONES = { diamond: "#4589FF", platinum: "#8A3FFC", rhodium: "#08BDBA", osmium: "#F1C21B" };
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function TierRoster() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState({ key: "tier_order", dir: "desc" });

  useEffect(() => { client.get("/ai/tiers/roster").then((r) => setData(r.data)).catch(() => setData({ error: true })); }, []);

  const rows = useMemo(() => {
    let list = [...(data?.customers || [])];
    if (filter !== "all") list = list.filter((c) => c.tier === filter);
    const { key, dir } = sort;
    list.sort((a, b) => {
      let av = a[key], bv = b[key];
      if (typeof av === "string") { av = av.toLowerCase(); bv = (bv || "").toLowerCase(); }
      if (av < bv) return dir === "asc" ? -1 : 1;
      if (av > bv) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [data, filter, sort]);

  const toggle = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
  const Th = ({ k, children, right }) => (
    <th className={`cursor-pointer select-none px-2 py-2 ${right ? "text-end" : "text-start"} hover:text-[var(--primary)]`}
      onClick={() => toggle(k)} data-testid={`roster-sort-${k}`}>
      <span className="inline-flex items-center gap-1">{children}<ArrowUpDown size={11} className={sort.key === k ? "opacity-100" : "opacity-30"} /></span>
    </th>
  );

  if (data?.error) return null;

  return (
    <Card className="mt-6" data-testid="tier-roster">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-head text-xl font-bold flex items-center gap-2" data-testid="tier-roster-title">
          <TrendingUp size={18} className="text-[var(--primary)]" /> Support tiers — revenue & who’s paying what
        </h2>
        <div className="flex items-center gap-4 text-sm">
          <span className="flex items-center gap-1" data-testid="roster-total-customers"><Users size={14} className="opacity-60" />{data?.total_customers ?? "…"} customers</span>
          <span className="flex items-center gap-1 font-semibold text-[#24A148]" data-testid="roster-total-mrr"><DollarSign size={14} />{money(data?.total_mrr)} MRR</span>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        {(data?.by_tier || []).map((t) => (
          <button key={t.key} onClick={() => setFilter(filter === t.key ? "all" : t.key)}
            className={`rounded-lg border p-3 text-start lift ${filter === t.key ? "ring-2" : ""}`}
            style={{ borderColor: TONES[t.key] + "55", boxShadow: filter === t.key ? `0 0 0 2px ${TONES[t.key]}55` : "none" }}
            data-testid={`roster-tier-${t.key}`}>
            <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: TONES[t.key] }}>{t.name}</div>
            <div className="mt-1 text-lg font-bold tabular-nums">{t.count}<span className="text-xs font-normal opacity-50"> @ ${t.monthly_usd.toFixed(2)}</span></div>
            <div className="text-xs text-[#24A148]">{money(t.mrr)}/mo</div>
          </button>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2 text-xs">
        <span className="opacity-60">Filter:</span>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-md border border-black/15 bg-transparent px-2 py-1 dark:border-white/15" data-testid="roster-filter">
          <option value="all">All tiers</option>
          {(data?.by_tier || []).map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
        </select>
        <span className="opacity-50">{rows.length} shown</span>
      </div>

      <div className="mt-2 max-h-96 overflow-auto">
        <table className="w-full text-sm" data-testid="roster-table">
          <thead className="sticky top-0 bg-[var(--bg,#fff)] text-xs opacity-70">
            <tr className="border-b border-black/10 dark:border-white/10">
              <Th k="name">Customer</Th>
              <Th k="email">Email</Th>
              <Th k="tier_order">Tier</Th>
              <Th k="monthly_usd" right>$/mo</Th>
              <Th k="parses_used" right>Parses</Th>
              <Th k="questions_used" right>Questions</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-b border-black/5 dark:border-white/5" data-testid={`roster-row-${c.id}`}>
                <td className="px-2 py-1.5 font-medium">{c.name}</td>
                <td className="px-2 py-1.5 opacity-70">{c.email}</td>
                <td className="px-2 py-1.5"><Badge style={{ background: TONES[c.tier] + "22", color: TONES[c.tier] }}>{c.tier_name}</Badge></td>
                <td className="px-2 py-1.5 text-end tabular-nums font-semibold">{c.monthly_usd.toFixed(2)}</td>
                <td className="px-2 py-1.5 text-end tabular-nums">{c.parses_used}/{c.parses_limit || "—"}</td>
                <td className="px-2 py-1.5 text-end tabular-nums">{c.questions_used}/{c.questions_limit}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="px-2 py-6 text-center text-xs opacity-50">No customers on this tier.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
